/** Single source of truth for in-app teaching microcopy.
 *
 *  Memso voice: intelligent, friendly, plain language, not cutesy. No
 *  em dashes, no emoji. 1-3 sentences. Action-oriented closing. Each
 *  entry's key is the banner_id passed to <TeachingBanner id=... />.
 */
export type TeachingTone = "info" | "tip" | "warning";

export interface TeachingEntry {
  title: string;
  body: string;
  tone?: TeachingTone;
}

export const TEACHING_CONTENT: Record<string, TeachingEntry> = {
  today_overview: {
    title: "Today",
    body: "Your live register for right now. Memso reorders it through the day as new captures land and priorities shift, so the most relevant things stay near the top. Skim it first thing.",
  },
  today_clusters: {
    title: "How Today is organised",
    body: "Memso groups your day into opinionated clusters: Work, Personal, Travel, Financial, Social and cultural. Each cluster shows only what is actionable now, not the raw inbox. Promotions, receipts, and system notices stay out. Pick a view at the top to focus on one cluster, or stay on All for the whole picture.",
    tone: "tip",
  },
  today_brief: {
    title: "The running brief",
    body: "Memso picks at most three threads worth your time right now and writes one short paragraph around them. News alerts, vendor marketing, and system notices are filtered out. The brief refreshes on its own; everything else lives one tap away in the engagement stream.",
  },
  today_things_to_finish: {
    title: "Things to finish",
    body: "The three threads Memso believes deserve a reply today, ranked by signal: deadlines first, then humans waiting on you, then close contacts you have not gotten back to. Tap Done when you reply, Snooze to defer, or Dismiss to let it drop.",
    tone: "tip",
  },
  today_recommendations: {
    title: "Suggestions",
    body: "Things Memso thinks are worth acting on, ranked by confidence. Accept to act on one, or Skip to dismiss it. Your choices train what surfaces next.",
    tone: "tip",
  },
  today_observations: {
    title: "Noticed",
    body: "Quiet cleanup-style notices: a stale thread, an unusual gap, a pattern worth a glance. They are low-stakes context, not tasks. Confirm the useful ones.",
  },
  today_engagements: {
    title: "Engagements",
    body: "Forward-dated commitments and prep arcs Memso is tracking for you. Open one to see the full activity history behind it.",
  },
  recall_overview: {
    title: "Recall",
    body: "Ask questions across everything you have captured and Memso answers with the sources attached. Open any source chip to see exactly what it drew from.",
    tone: "tip",
  },
  weave_overview: {
    title: "Weave",
    body: "Your knowledge graph: the people, projects, and topics Memso has connected from your captures. Dotted links are inferred and become solid once you confirm them.",
  },
  people_overview: {
    title: "People",
    body: "Everyone Memso has seen across your connectors. A dashed avatar means the person is inferred and not yet confirmed; confirm to lock the name and relationship in.",
  },
  engagements_overview: {
    title: "Engagements",
    body: "Multi-step arcs Memso assembles from related captures, like a trip or a deal. Each one collects the prep, people, and deadlines in one place.",
  },
  engagements_detail: {
    title: "Activity history",
    body: "Every capture Memso tied to this engagement, in order. Use it to catch up fast before the next step.",
  },
  commitments_overview: {
    title: "Commitments",
    body: "Promises Memso extracted from your conversations, both the ones you made and the ones made to you. Confirm the real ones so follow-ups stay accurate.",
  },
  goals_overview: {
    title: "Goals",
    body: "Your own annual and quarterly goals, written by you, not inferred. Memso uses them to weigh what it surfaces. Add a goal to steer the system.",
    tone: "tip",
  },
  reflections_overview: {
    title: "Reflections",
    body: "A quick end-of-day capture for what mattered and what is on your mind. It feeds tomorrow's brief. Write a line whenever you want.",
  },
  wiki_autocomplete_tip: {
    title: "Link as you type",
    body: "Type [[ to link to a person, project, or topic. Memso autocompletes from your data and can create a new one inline.",
    tone: "tip",
  },
  forgotten_overview: {
    title: "Forgotten threads",
    body: "Conversations that went quiet while the ball was in your court. Confirm to draft a follow-up, or dismiss if it no longer matters.",
    tone: "tip",
  },
  conversations_overview: {
    title: "Conversations",
    body: "A cached summary per thread so you can re-enter a long exchange without rereading it. Open one to see key points and open commitments.",
  },
  subscriptions_overview: {
    title: "Subscriptions",
    body: "Recurring vendors Memso detected from receipts and confirmations, with cancel suggestions where it spots waste. Confirm the ones that are real.",
  },
  composer_overview: {
    title: "Composer",
    body: "Drafts a message grounded in the thread's history and your intent. Memso never sends; you copy it out or open a pre-filled mail link.",
    tone: "tip",
  },
  identity_review_overview: {
    title: "Identity review",
    body: "Memso found people who look like the same person across connectors. Confirm a merge to unify them, or keep them separate.",
  },
  inference_needs_review_overview: {
    title: "Needs review",
    body: "Anything Memso inferred but is not sure about. Confirm to make it solid and trusted, reject to remove it. Every choice sharpens future inferences.",
    tone: "tip",
  },
  scope_filter: {
    title: "View scopes",
    body: "Scopes split your life into views like personal, work, and private. Switching the view filters every surface to that slice; private stays hidden unless you ask for it.",
  },
  inferred_vs_confirmed_chip: {
    title: "Why is this dashed",
    body: "A dashed chip is something Memso inferred but you have not confirmed yet. Tap the check to confirm it, or the x to reject. Confirmed items render solid.",
  },
  settings_connectors_overview: {
    title: "Connectors",
    body: "Each connector is a source Memso captures from, like mail, messages, or a data export. Set one up to start weaving that source into your register.",
  },
  settings_admin_reset: {
    title: "Danger zone",
    body: "These actions rebuild or wipe local derived data. Your upstream accounts are never touched, but local synthesis is regenerated from scratch. Proceed only if you mean it.",
    tone: "warning",
  },
  recommendations_overview: {
    title: "Recommendations",
    body: "One place to triage everything Memso thinks you should act on. Accept routes it to the right handler; Skip clears it. Work top down by confidence.",
  },
  observations_overview: {
    title: "Observations",
    body: "Ambient patterns Memso noticed across your captures. They are context, not chores. Confirm the ones that ring true so the signal improves.",
  },
  actions_overview: {
    title: "Actions",
    body: "A unified tracker for everything Memso turned into something actionable. Each row shows where it came from and what happens when you accept it.",
  },
  now_mode: {
    title: "Now",
    body: "Your live register for the present: what is coming up, what Memso suggests, what it noticed, and threads going quiet. Skim it first; it reorders itself through the day.",
  },
  capture_mode: {
    title: "Capture",
    body: "Every way to put something into Memso: a quick thought, a daily reflection, a draft, or a deliberate goal, wish, or decision. The plus button works from anywhere.",
    tone: "tip",
  },
  explore_mode: {
    title: "Explore",
    body: "Ask Memso anything and browse what it has woven: people, places, projects, the graph, your timeline, and your own profile. Start with Recall or open the Library.",
    tone: "tip",
  },
  act_mode: {
    title: "Act",
    body: "Where commitments turn into motion: engagement arcs, your goals, the unified action tracker, and the routines you keep. Work it top down.",
  },
  communicate_mode: {
    title: "Communicate",
    body: "Outbound follow-through: subscriptions worth cancelling, threads to revive, drafts to send, and relationships cooling off. Memso prepares, you decide.",
  },
  pulse_mode: {
    title: "Pulse",
    body: "The health of the system itself: flow of captures, the worker fleet, connector status, and what your paired tools have been reading. Glance here when something feels off.",
  },
  engagement_stream_overview: {
    title: "Engagement stream",
    body: "Your live triage view. Up Next is what is starting soon, Awaiting You is where someone is waiting on a reply, Going Cold is active threads that have gone quiet, and Recently Active is the ambient backdrop. Open any row to see the full thread.",
  },
  capture_quick_overview: {
    title: "Quick capture",
    body: "Drop a thought, task, note, or link and Memso weaves it into the right place. Voice, image, and screenshot inputs work here too. The plus button on the bottom bar opens this from anywhere.",
    tone: "tip",
  },
  agenda_overview: {
    title: "Agenda",
    body: "What is coming up across meetings, dates, deadlines, and trips. Memso assembles it from your captures and connectors, then groups it so you can scan one block at a time.",
  },
  timeline_overview: {
    title: "Timeline",
    body: "Your chronology of what already happened, grouped by month. Pick a year to scroll back; the active view scope filters every row.",
  },
  wishes_overview: {
    title: "Wishes",
    body: "Undated things you want to do someday. Capture them lightweight; when one gets a date, convert it to a soft target so it lands on your agenda.",
    tone: "tip",
  },
  things_overview: {
    title: "Things",
    body: "Products, devices, and objects Memso has formalized from receipts and confirmations. The list fills out as transactions land; nothing here is manual.",
  },
  transactions_overview: {
    title: "Transactions",
    body: "Your financial activity, surfaced from receipts, statements, and confirmations. Filter by category to spot patterns; week and month rollups sit on top.",
  },
  me_overview: {
    title: "Me",
    body: "A mirror Memso holds up: who it thinks you are, your strongest people, orgs, topics, places, and a 90-day activity heatmap. Reflect, then steer.",
  },
  orgs_overview: {
    title: "Orgs",
    body: "Every organization Memso has woven from your captures, with category and last-seen. Search to jump to one; tap to see the dossier and people inside.",
  },
  places_overview: {
    title: "Places",
    body: "Everywhere Memso has resolved from your messages and captures, ranked by visits or recency. Filter by city, country, or landmark.",
  },
  person_dossier_overview: {
    title: "Person dossier",
    body: "Everything Memso has woven about one person on a single canvas: contact rhythm, sentiment, open commitments, and recent conversations. The relationship snapshot is the LLM's read; confirm or correct it.",
  },
  place_dossier_overview: {
    title: "Place dossier",
    body: "Everything Memso has tied to this place: visits, mentions, people seen here, and any media. Confirm the inferred bits so the place graph stays sharp.",
  },
  org_detail_overview: {
    title: "Org dossier",
    body: "Memso's view of one organization: the people you know there, recent threads, and what it has shipped you. Use it as the entry point before you reach out.",
  },
  trip_detail_overview: {
    title: "Trip",
    body: "Memso assembled this trip from travel signals like flight confirms, hotel emails, and calendar events. Confirm the trip to lock it in; reject if it is not real.",
  },
  flow_overview: {
    title: "Flow",
    body: "How captures move through Memso: throughput per source, downstream entities created, and any anomalies. Glance here when a source feels stuck.",
  },
  status_overview: {
    title: "System status",
    body: "Live health of every dependency Memso talks to, plus the bundle and build identity of the running daemon. Polls every ten seconds.",
  },
  perf_overview: {
    title: "Perf",
    body: "Codified invariants Memso checks on a schedule. Each row shows pass/warn/critical with a 24h sparkline and whatever autoheal action ran. Pair it with Status when something is off.",
  },
  health_overview: {
    title: "Health",
    body: "Health-related things Memso noticed across your captures: symptoms, sleep, vitals, exercise. Each one is dotted until you confirm it, so noise never compounds.",
  },
  patterns_overview: {
    title: "Patterns",
    body: "Recurring rhythms Memso has spotted in when and how you do things. Confirm the ones that match how you actually live so the model sharpens.",
  },
  routines_overview: {
    title: "Routines",
    body: "The cadences you keep, like a weekly run or a monthly review. Log when you perform one to keep the streak honest; confirm inferred routines or reject them.",
  },
  decisions_overview: {
    title: "Decisions",
    body: "A pros and cons board for what you are weighing, and an archive of what you decided with rationale. Add a new decision to track one you have not made yet.",
    tone: "tip",
  },
  preferences_overview: {
    title: "Preferences",
    body: "Likes and dislikes Memso inferred from your captures, grouped by domain. Confirm the ones that ring true; reject what does not.",
  },
  routines_log_tip: {
    title: "Log when you do it",
    body: "Tap the check to record that you just performed a routine. Memso uses the timestamp to track cadence drift over weeks.",
    tone: "tip",
  },
  intents_overview: {
    title: "Intents",
    body: "Things you want to do, watch, read, try, or buy, organized into kanban columns by kind. Each card is dotted until confirmed; research cards expand to show what the agent found.",
  },
  backlog_overview: {
    title: "Backlog",
    body: "User-triggered batch jobs that work historical data, like a Gmail backfill or a reanalyze pass. Every action shows an estimate before it spends real cost.",
    tone: "tip",
  },
  composer_page_overview: {
    title: "Composer",
    body: "Drafts a message grounded in the thread's history and your intent. Memso never sends. Copy the draft into your mail client or open a pre-filled mailto link.",
    tone: "tip",
  },
  brainstorm_overview: {
    title: "Brainstorm",
    body: "Chat with Memso over your own captures. Each answer pulls the top sources into the prompt and shows them as citations below the reply.",
    tone: "tip",
  },
  needs_attention_overview: {
    title: "Needs attention",
    body: "Relationships where the recent tone is declining or persistently negative. Read-only triage so you can choose to reach out before a thread truly cools.",
  },
  needs_review_people_overview: {
    title: "Needs review",
    body: "Pending validations on people, relationships, tags, scopes, and merges. Drain it at your own pace; every confirm and reject teaches the model what to do next time.",
    tone: "tip",
  },
  unified_actions_overview: {
    title: "All actions",
    body: "Every actionable item Memso surfaced, in one tracker. Accept runs the per-source handler in place; Dismiss and Snooze are generic. Filter by surface or status to focus.",
  },
  agentic_actions_overview: {
    title: "Agentic runs",
    body: "Multi-step agent runs like Plan my day or Weekly review. Watch steps advance and answer prompts inline. Memso never auto-sends; any external step pauses for your confirmation.",
  },
  actions_queue_overview: {
    title: "Actions queue",
    body: "Recently surfaced actions still waiting for a call. Triage from the top; accepted ones move into the unified tracker.",
  },
  teams_overview: {
    title: "Teams",
    body: "Reconciliation for Microsoft Teams threads, grouped by day. Today auto-scans on open; older days run on demand. Memso flags asks you owe and promises you made.",
  },
  whatsapp_overview: {
    title: "WhatsApp",
    body: "Reconciliation for WhatsApp messages, scoped per conversation. The first card scans recent days for asks you owe or promises you made without closure.",
  },
  android_messages_overview: {
    title: "Android messages",
    body: "Connector for SMS, RCS, and call history captured by the Memso Companion app. Pair a phone first; messages stream in once paired.",
  },
  linkedin_overview: {
    title: "LinkedIn",
    body: "LinkedIn has no usable personal-data API, so this is an archive import. Download your data from LinkedIn, then upload the zip here. Read-only: Memso never DMs or reacts.",
  },
  simplifi_overview: {
    title: "Simplifi",
    body: "Connector for Quicken Simplifi. Paste your account credentials to pull transactions into the financial ledger. Read-only: Memso never moves money.",
  },
  outlook_bridge_configure_overview: {
    title: "Outlook bridge",
    body: "Sets the URL of the bridge running inside your Windows VM. Paste the address you see in the VM, then run Test Connection to confirm the bridge build matches.",
  },
  outlook_bridge_test_overview: {
    title: "Outlook bridge test",
    body: "Live probe of the bridge inside your Windows VM. Use it to confirm the bridge is reachable, the build matches, and the COM proxy is healthy.",
  },
  companion_pair_overview: {
    title: "Pair your phone",
    body: "Generates a one-time pairing token for the Memso Android Companion app. The token shows once; we only keep its hash. Existing devices appear below.",
  },
  settings_overview: {
    title: "Settings",
    body: "Everything that configures Memso for you: profile, theme, connectors, AI routing, voice, and admin actions. Each section is one tap deep.",
  },
  settings_ai_overview: {
    title: "AI routing",
    body: "One row per AI feature, three knobs each: provider, model, and think. Cloud features default to the cheapest Gemini Flash variant; private and mail features stay local. Saves apply within a minute.",
  },
  settings_ai_features_overview: {
    title: "Feature prompts",
    body: "The long-form prompt and call-settings editor for each AI feature. Override the catalog default, save, and Memso uses your version on the next call.",
  },
  context_settings_overview: {
    title: "Context",
    body: "Opt-in location, weather, and activity signals that sharpen what Memso suggests. Named places like home and work are managed here. Context never leaves your device unless you turn on sync.",
  },
  cultural_profile_overview: {
    title: "Cultural profile",
    body: "Country, heritage, religion, and household basics that help Memso enrich your calendar with the right holidays and observances. Edit anytime; the next worker pass picks it up.",
  },
  reflect_history_overview: {
    title: "Reflection history",
    body: "Every past reflection, newest first, each expandable into the full entry. Memso uses these to color what surfaces tomorrow.",
  },
  observations_page_overview: {
    title: "Observations",
    body: "Ambient patterns Memso has noticed across your captures. They are context, not chores. Confirm the ones that ring true; the signal sharpens.",
  },
  explore_overview: {
    title: "Explore lens",
    body: "Switch between Recall, People, Relationships, Knowledge graph, Weave, Dates, and About you. Each lens reframes the same data; pick what matches your question.",
  },
  recall_page_overview: {
    title: "Recall",
    body: "Ask Memso anything across everything you have captured. Answers come with the sources attached; open a chip to see exactly what fed the reply.",
    tone: "tip",
  },
  knowledge_graph_overview: {
    title: "Knowledge graph",
    body: "An interactive map of the people, projects, and topics Memso has connected. Drag to pan, scroll to zoom, click a node to focus.",
  },
  dates_overview: {
    title: "Dates",
    body: "Birthdays, anniversaries, deadlines, and other dated events Memso wove from your captures. Confirm inferred ones to keep your calendar accurate.",
  },
  about_you_overview: {
    title: "About you",
    body: "What Memso has learned about you across captures: identity, role, the topics you care about, and the people closest to your work.",
  },
  relationships_overview: {
    title: "Relationships",
    body: "Your network as Memso sees it, with sentiment and recency per person. Use it to spot connections that are cooling off before they go quiet.",
  },
  login_overview: {
    title: "Sign in",
    body: "Memso uses Google for sign-in. After you authorize, you land on the workspace under your handle. Nothing ingests until you connect a source.",
  },
  onboarding_overview: {
    title: "Welcome to Memso",
    body: "Six quick steps: pick a handle, pick a theme, connect a source, set your scopes, and you are in. Skip what you do not need; you can revisit any step from Settings.",
    tone: "tip",
  },
};

export type TeachingId = keyof typeof TEACHING_CONTENT;
